import React, { useState, useEffect } from 'react'
import { shareService, ShareDetails } from '../services/shareService'
import ZenViewerSimple from './ZenViewerSimple'
import MobileShareSlideshow from './MobileShareSlideshow'
import { useIsMobile } from '../hooks/useIsMobile'

interface ShareViewerProps {
  shareId: string
}

const ShareViewer: React.FC<ShareViewerProps> = ({ shareId }) => {
  const isMobile = useIsMobile()
  const [shareData, setShareData] = useState<ShareDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string>('')

  useEffect(() => {
    const loadShare = async () => {
      if (!shareId) {
        setError('Invalid share ID')
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const data = await shareService.getShare(shareId)
        setShareData(data)
      } catch (err) {
        console.error('Failed to load share:', err)
        setError(err instanceof Error ? err.message : 'Failed to load share')
      } finally {
        setLoading(false)
      }
    }

    loadShare()
  }, [shareId])

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="animate-spin w-8 h-8 border-2 border-white/20 border-t-white rounded-full mx-auto mb-4"></div>
          <p className="text-lg">Loading shared translation...</p>
        </div>
      </div>
    )
  }

  if (error || !shareData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-6 text-center">
            <div className="text-red-400 text-4xl mb-4">⚠️</div>
            <h1 className="text-xl font-semibold text-white mb-2">Share Not Found</h1>
            <p className="text-gray-300 mb-4">
              {error || 'The requested share could not be found or has been removed.'}
            </p>
            <button
              onClick={() => window.location.href = '/'}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Create New Translation
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Mobile: Auto-enter immersive Pinterest-style slideshow
  // Desktop: Use traditional tabbed viewer
  if (isMobile) {
    return <MobileShareSlideshow shareData={shareData} />
  }

  // Desktop: Use dedicated viewer component for clean, read-only display
  return (
    <ZenViewerSimple
      sourceLanguage={shareData.sourceLanguage}
      targetLanguage={shareData.targetLanguage}
      htmlContent={shareData.htmlContent}
    />
  )
}

export default ShareViewer